import { NoxnLogo } from "@/components/noxn/NoxnLogo";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mail, Check, Lock } from "lucide-react";

import { useAuth, getDeviceToken, storeDeviceToken } from "@/lib/auth";
import { usePageMeta } from "@/hooks/usePageMeta";

const CODE_LEN = 6;
const INK = "#1E3A6F";
const ACCENT = "#FF5A00";
const INK_TINT = (a) => `rgba(30,58,111,${a})`;

/**
 * NOXN sign-in — light theme matching the /v2 landing.
 * Restraint over decoration: single centred column, editorial headline,
 * one primary CTA per step, orange accent used sparingly.
 */
export default function Login() {
    usePageMeta({ title: "Sign in · NOXN", description: "Sign in to your NOXN account." });
    const { user, requestCode, verifyCode, loginStart, loginPassword, trustDevice } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const nextParam = params.get("next");

    const [step, setStep] = useState("contact"); // contact | password | code
    const [contact, setContact] = useState("");
    const [password, setPwd] = useState("");
    const [usedPassword, setUsedPassword] = useState(false);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [remember, setRemember] = useState(true);
    const codeRef = useRef(null);
    const pwdRef = useRef(null);

    const destFor = (u) => nextParam || (u?.role && u.role !== "client" ? "/dashboard" : "/account");

    useEffect(() => {
        if (user) navigate(destFor(user), { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        if (!cooldown) return;
        const t = setTimeout(() => setCooldown((v) => Math.max(0, v - 1)), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const gotoCode = () => {
        setStep("code");
        setCooldown(30);
        setTimeout(() => codeRef.current?.focus(), 60);
        toast.success("Code sent to your email.");
    };

    const submitContact = async (e) => {
        e?.preventDefault?.();
        const c = contact.trim();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c)) { toast.error("Enter a valid email."); return; }
        setBusy(true);
        try {
            const res = await loginStart(c, getDeviceToken(c));
            if (res.exists === false) {
                toast.info("No account found for that email — create your free account.");
                navigate(`/signup?email=${encodeURIComponent(c)}`); return;
            }
            if (res.mode === "code") { setUsedPassword(false); gotoCode(); }
            else { setStep("password"); setTimeout(() => pwdRef.current?.focus(), 60); }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not sign you in.");
        } finally { setBusy(false); }
    };

    const submitPassword = async (e) => {
        e?.preventDefault?.();
        if (!password) { toast.error("Enter your password."); return; }
        setBusy(true);
        try {
            await loginPassword(contact.trim(), password);
            setUsedPassword(true);
            gotoCode();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid email or password.");
        } finally { setBusy(false); }
    };

    const resendCode = async () => {
        setBusy(true);
        try {
            await requestCode(contact.trim()); setCooldown(30); toast.success("New code sent.");
        } catch (err) { toast.error("Could not resend the code."); }
        finally { setBusy(false); }
    };

    const submitCode = async (e) => {
        e?.preventDefault?.();
        if (code.length !== CODE_LEN) return;
        setBusy(true);
        try {
            const u = await verifyCode(contact.trim(), code, remember);
            if (usedPassword) {
                try {
                    const token = await trustDevice();
                    storeDeviceToken(contact.trim(), token);
                } catch (err) { /* non-fatal */ }
            }
            toast.success("Welcome back.");
            navigate(destFor(u), { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not verify the code.";
            toast.error(typeof msg === "string" ? msg : "Could not verify the code.");
            setCode("");
        } finally { setBusy(false); }
    };

    /* ─────────────────────────── shared field styles ─────────────────── */
    const fieldWrap = (invalid) => ({
        borderColor: invalid ? "#EF4444" : INK_TINT(0.18),
        boxShadow: "0 1px 2px rgba(30,58,111,0.04)",
    });

    return (
        <div className="min-h-screen bg-white font-body" style={{ color: INK }} data-testid="login-page">
            {/* Header */}
            <header className="fixed inset-x-0 top-0 z-40 bg-white/85 backdrop-blur border-b" style={{ borderColor: INK_TINT(0.08) }}>
                <div className="mx-auto max-w-5xl px-6 lg:px-10 h-16 flex items-center justify-between">
                    <Link to="/" data-testid="login-back" className="flex items-center gap-3 group">
                        <NoxnLogo variant="navy" className="h-5 w-auto transition-transform duration-300 group-hover:-rotate-1" />
                    </Link>
                    <Link to="/signup" data-testid="login-signup-link"
                        className="text-[13px] transition-colors" style={{ color: INK_TINT(0.7) }}>
                        <span className="hover:text-[color:var(--ink)]" style={{ ["--ink"]: INK }}>Create account →</span>
                    </Link>
                </div>
            </header>

            <main className="max-w-md mx-auto px-6 pt-40 pb-24 relative">
                {/* Ambient orange whisper — matches the /v2 hero cadence */}
                <div className="pointer-events-none absolute -top-24 -right-16 w-[380px] h-[380px] rounded-full opacity-25 blur-[100px]"
                    style={{ background: `radial-gradient(circle, ${ACCENT}66, transparent 60%)` }} />

                {/* Pre-header dot */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="text-[11px] uppercase tracking-[0.28em] mb-6 inline-flex items-center gap-2"
                    style={{ color: INK_TINT(0.55) }}
                >
                    <motion.span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ACCENT }}
                        animate={{ opacity: [1, 0.35, 1], scale: [1, 1.2, 1] }} transition={{ duration: 2.4, repeat: Infinity }} />
                    {step === "contact" ? "Sign in" : step === "password" ? "New device" : "Almost in"}
                </motion.div>

                <AnimatePresence mode="wait">
                    {/* ─── Step 1: contact ─── */}
                    {step === "contact" && (
                        <motion.form key="contact" onSubmit={submitContact}
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            data-testid="login-step-contact"
                        >
                            <h1 className="font-display tracking-[-0.03em] text-5xl leading-[0.98] font-medium" style={{ color: INK }}>
                                Welcome<br />
                                <span style={{ color: ACCENT }}>back.</span>
                            </h1>
                            <p className="mt-6 text-[15px] leading-relaxed" style={{ color: INK_TINT(0.65) }}>
                                Use your email — we&apos;ll send a one-time code.
                            </p>

                            {/* Email input */}
                            <div className="mt-10">
                                <label className="text-[11px] uppercase tracking-[0.28em]" style={{ color: INK_TINT(0.55) }}>
                                    Email address
                                </label>
                                <div className="mt-2 flex items-center gap-2 rounded-2xl border px-4 py-3.5 transition-all focus-within:border-[color:var(--acc)]"
                                    style={{ ...fieldWrap(false), ["--acc"]: ACCENT }}>
                                    <Mail size={15} style={{ color: INK_TINT(0.55) }} />
                                    <input
                                        type="email"
                                        value={contact}
                                        onChange={(e) => setContact(e.target.value)}
                                        placeholder="you@company.com"
                                        data-testid="login-contact-input"
                                        autoFocus
                                        className="flex-1 bg-transparent outline-none text-sm placeholder:text-[color:var(--ph)]"
                                        style={{ color: INK, ["--ph"]: INK_TINT(0.35) }}
                                    />
                                </div>
                            </div>

                            <PrimaryBtn busy={busy} data-testid="login-send-code-btn">
                                {busy ? <><Loader2 size={16} className="animate-spin" /> Checking</> : <>Continue <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>}
                            </PrimaryBtn>

                            <p className="mt-5 text-[12px]" style={{ color: INK_TINT(0.55) }}>
                                No account yet?{" "}
                                <Link to="/signup" className="underline" style={{ color: INK, textDecorationColor: INK_TINT(0.4) }}>Create one free</Link>
                                {" · "}
                                <Link to="/book" className="underline" style={{ color: INK, textDecorationColor: INK_TINT(0.4) }}>Book as guest</Link>
                            </p>
                        </motion.form>
                    )}

                    {/* ─── Step 2: password ─── */}
                    {step === "password" && (
                        <motion.form key="password" onSubmit={submitPassword}
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            data-testid="login-step-password"
                        >
                            <h1 className="font-display tracking-[-0.03em] text-5xl leading-[0.98] font-medium" style={{ color: INK }}>
                                New device<br /><span style={{ color: ACCENT }}>detected.</span>
                            </h1>
                            <p className="mt-6 text-[15px] break-all" style={{ color: INK_TINT(0.65) }}>
                                Enter the password for <strong style={{ color: INK }}>{contact}</strong>.
                            </p>

                            <div className="mt-10">
                                <label className="text-[11px] uppercase tracking-[0.28em]" style={{ color: INK_TINT(0.55) }}>Password</label>
                                <div className="mt-2 flex items-center gap-2 rounded-2xl border px-4 py-3.5 transition-all focus-within:border-[color:var(--acc)]"
                                    style={{ ...fieldWrap(false), ["--acc"]: ACCENT }}>
                                    <Lock size={15} style={{ color: INK_TINT(0.55) }} />
                                    <input ref={pwdRef} type="password" value={password} onChange={(e) => setPwd(e.target.value)}
                                        placeholder="••••••••" data-testid="login-password-input"
                                        className="flex-1 bg-transparent outline-none text-sm placeholder:text-[color:var(--ph)]"
                                        style={{ color: INK, ["--ph"]: INK_TINT(0.35) }}
                                    />
                                </div>
                            </div>

                            <div className="mt-4 text-[12px]">
                                <button type="button" onClick={() => { setStep("contact"); setPwd(""); }}
                                    className="underline transition-colors"
                                    style={{ color: INK_TINT(0.7), textDecorationColor: INK_TINT(0.3) }}
                                >
                                    ← Change email
                                </button>
                            </div>

                            <PrimaryBtn busy={busy || !password} data-testid="login-password-btn">
                                {busy ? <><Loader2 size={16} className="animate-spin" /> Checking</> : <>Continue <Lock size={15} /></>}
                            </PrimaryBtn>
                        </motion.form>
                    )}

                    {/* ─── Step 3: code ─── */}
                    {step === "code" && (
                        <motion.form key="code" onSubmit={submitCode}
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            data-testid="login-step-code"
                        >
                            <h1 className="font-display tracking-[-0.03em] text-5xl leading-[0.98] font-medium" style={{ color: INK }}>
                                Check your<br />
                                <span style={{ color: ACCENT }}>inbox.</span>
                            </h1>
                            <p className="mt-6 text-[15px] break-all" style={{ color: INK_TINT(0.65) }}>
                                Sent a 6-digit code to <strong style={{ color: INK }}>{contact}</strong>.
                            </p>

                            <div className="mt-10">
                                <label className="text-[11px] uppercase tracking-[0.28em]" style={{ color: INK_TINT(0.55) }}>One-time code</label>
                                <input
                                    ref={codeRef} type="text" inputMode="numeric" pattern="[0-9]*" maxLength={CODE_LEN}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LEN))}
                                    placeholder="••••••"
                                    data-testid="login-code-input"
                                    className="mt-2 w-full rounded-2xl border px-4 py-4 text-3xl font-mono-tech tracking-[0.6em] text-center outline-none transition-colors"
                                    style={{ ...fieldWrap(false), color: INK, backgroundColor: "#FFFFFF" }}
                                />
                            </div>

                            <div className="mt-4 flex items-center justify-between text-[12px]">
                                <button type="button" onClick={() => { setStep("contact"); setCode(""); setPwd(""); }}
                                    className="underline transition-colors"
                                    style={{ color: INK_TINT(0.7), textDecorationColor: INK_TINT(0.3) }}
                                >
                                    ← Change email
                                </button>
                                <button type="button" onClick={resendCode} disabled={cooldown > 0 || busy}
                                    data-testid="login-resend-btn"
                                    className="transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{ color: cooldown > 0 ? INK_TINT(0.4) : ACCENT }}
                                >
                                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                                </button>
                            </div>

                            <label className="mt-6 inline-flex items-center gap-2 cursor-pointer select-none" data-testid="login-remember-label">
                                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                                    data-testid="login-remember-checkbox" className="accent-[#FF5A00] w-4 h-4" />
                                <span className="text-[13px]" style={{ color: INK_TINT(0.75) }}>Remember me for 30 days</span>
                            </label>

                            <PrimaryBtn busy={busy || code.length !== CODE_LEN} data-testid="login-verify-btn">
                                {busy ? <><Loader2 size={16} className="animate-spin" /> Verifying</> : <>Verify <Check size={16} /></>}
                            </PrimaryBtn>
                        </motion.form>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
}

/**
 * Shared primary CTA — matches the /v2 hero button (rounded pill, navy →
 * orange on hover with a warm shadow lift). Passes through `busy` as the
 * disabled state so callers stay expressive.
 */
const PrimaryBtn = ({ busy, children, ...rest }) => (
    <button
        type="submit"
        disabled={busy}
        {...rest}
        className="group mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full py-4 text-sm font-medium text-white transition-all disabled:opacity-60"
        style={{ backgroundColor: INK }}
        onMouseEnter={(e) => {
            if (!e.currentTarget.disabled) {
                e.currentTarget.style.backgroundColor = ACCENT;
                e.currentTarget.style.boxShadow = "0 18px 40px -14px rgba(255,90,0,0.55)";
            }
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = INK;
            e.currentTarget.style.boxShadow = "";
        }}
    >
        {children}
    </button>
);
